// @flow
/** @jsx jsx */

import { PaymentMethodImage } from '@zapier/payment';
import { getCardDefinitionFromBrand } from '@zapier/payment/src/utils';
import { jsx } from '@emotion/core';
import {
  type CreditCardBrandCode,
  type CreditCardDefinition,
  type PaymentSourceMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';

const CARD_DEFINITIONS: CreditCardDefinition[] = [
  getCardDefinitionFromBrand('Visa'),
  getCardDefinitionFromBrand('MasterCard'),
  getCardDefinitionFromBrand('American Express'),
  getCardDefinitionFromBrand('Discover'),
];

type Props = {
  /** Show small version of images */
  isSmall?: boolean,
  methods: Array<PaymentSourceMethod>,
  /** All other payment method icons will be greyed out */
  selectedMethod?: ?CreditCardBrandCode,
};

const wrapperCss = {
  'span + span, span + svg': {
    marginLeft: 15,
  },
};

const PaymentMethodImageStrip = ({
  isSmall = false,
  methods = [],
  selectedMethod,
}: Props) => {
  // Workaround for https://github.com/braintree/credit-card-type/pull/73
  // Braintree's HostedFields API returns `master-card` as the card type
  // but `credit-card-type` package returns `mastercard`
  const selectedMethodType =
    selectedMethod === 'master-card' ? 'mastercard' : selectedMethod;

  const cards = CARD_DEFINITIONS.map(cardDefinition => (
    <PaymentMethodImage
      imageType={cardDefinition.type}
      isDisabled={
        selectedMethodType && selectedMethodType !== cardDefinition.type
      }
      isSmall={isSmall}
      key={cardDefinition.type}
    />
  ));

  const images = methods.reduce((acc, method) => {
    switch (method) {
      case paymentSourceMethods.card:
        return [...acc, ...cards];
      case paymentSourceMethods.paypal:
        return [
          ...acc,
          <PaymentMethodImage
            imageType={paymentSourceMethods.paypal}
            isSmall={isSmall}
            key={paymentSourceMethods.paypal}
          />,
        ];
      case paymentSourceMethods.bank:
        return [
          ...acc,
          <PaymentMethodImage
            imageType={paymentSourceMethods.bank}
            isSmall={isSmall}
            key={paymentSourceMethods.bank}
          />,
        ];
      default:
        return acc;
    }
  }, []);

  return <div css={wrapperCss}>{images}</div>;
};

export default PaymentMethodImageStrip;
